import React, { lazy, Suspense, useState } from 'react';
import { Link } from 'react-router-dom';
import cn from 'clsx';

import Button from '../../../../library/button/button';
import { CheckboxGroup } from '../../../../library/checkbox/checkbox';
import Tooltip from '../../../../library/tooltip/tooltip';
import {
  MapMarkerAltSolidIcon,
  PenLightIcon
} from '../../../../shared/icons/icons';
import Chip from '../../../chip/chip';
import CategoryCollapsible from '../category-collapsible/category-collapsible';
import FilterOption from '../filter-option/filter-option';

import { useAemContent, useMyStore } from '../../../../shared/hooks/hooks';

import { trackAction } from '../../../../shared/utils/analytics/analytics';
import { isArrayWithLength } from '../../../../shared/utils/validators/validators';

import {
  AEM_CONTENT_IDS,
  AEM_CONTENT_PAGE_IDS
} from '../../../../shared/constants/cms';
import { FILTER_KEYS } from '../../../../shared/constants/filters';

import styles from './get-it-fast-category.scss';

interface FilterValue {
  categoryCode: string;
  categoryName: string;
  valueCode: string;
  valueName: string;
}

interface GetItFastCategoryProps {
  filterValues: FilterValue[];
  isChangeStoreDisabled: boolean;
  onClick: () => void;
  onSaveClick: (value: boolean) => void;
  selectedFilters: FilterValue[];
  toggleType: string;
}

const PreferredStoreSearch = lazy(
  () => import('../preferred-store-search/preferred-store-search')
);

function GetItFastCategory(props: GetItFastCategoryProps) {
  const {
    filterValues,
    isChangeStoreDisabled = false,
    onClick,
    onSaveClick,
    selectedFilters,
    toggleType
  } = props;
  const sortedFilterValues = filterValues.sort((a, b) =>
    a.categoryCode.localeCompare(b.categoryCode)
  );

  const [showStoreSearch, setShowStoreSearch] = useState(false);
  const { myStore, nearbyStorePreferences, updateNearbyStoreSelection } =
    useMyStore();

  const { aemTooltipsContent } = useAemContent(
    AEM_CONTENT_PAGE_IDS.treadwellPlp
  );
  const otherStoresCopy = aemTooltipsContent?.[AEM_CONTENT_IDS.otherStores];

  const { aemTooltipsContent: aemTooltipsContentMultiStore } = useAemContent(
    AEM_CONTENT_PAGE_IDS.global
  );
  const preferredStoryCopy =
    aemTooltipsContentMultiStore?.[AEM_CONTENT_IDS.plpMultiStore]?.tipText;

  function handleNearbyStoreUpdate(filter) {
    const { valueCode } = filter;
    updateNearbyStoreSelection(valueCode);
  }

  function getLabel(filterValue) {
    if (filterValue.categoryCode === FILTER_KEYS.STORE_IN_STOCK) {
      return 'Available now';
    }

    return filterValue.valueName;
  }

  function isSelectedFilter(filterValue) {
    if (!Array.isArray(selectedFilters)) {
      return false;
    }

    return Boolean(
      selectedFilters.find(
        selectedFilter =>
          selectedFilter.valueCode === filterValue.valueCode &&
          selectedFilter.categoryCode === filterValue.categoryCode
      )
    );
  }

  if (
    !isArrayWithLength(sortedFilterValues) &&
    (!isArrayWithLength(myStore?.nearByStores) || isChangeStoreDisabled)
  ) {
    return null;
  }

  return (
    <CategoryCollapsible
      isOpenByDefault
      label="Get it Fast"
      toggleType={toggleType}
    >
      <div
        className={cn(styles['container'], 'get-it-fast-category__container')}
      >
        {showStoreSearch ? (
          <Suspense fallback={<div />}>
            <PreferredStoreSearch
              onCancelClick={() => setShowStoreSearch(false)}
              onSaveClick={() => onSaveClick(true)}
            />
          </Suspense>
        ) : (
          <>
            {!isChangeStoreDisabled && (
              <div>
                <div className={styles['preferred-store-heading']}>
                  <span>
                    <MapMarkerAltSolidIcon
                      className={styles['preferred-store-icon']}
                    />{' '}
                    Your preferred store{' '}
                    <Tooltip content={preferredStoryCopy} />
                  </span>

                  <Button
                    onClick={() => {
                      setShowStoreSearch(true);
                      trackAction('plp_multistore_change_store_click');
                    }}
                  >
                    <PenLightIcon /> change
                  </Button>
                </div>

                {myStore && (
                  <>
                    <div className={styles['preferred-store-address']}>
                      {myStore.address?.line1?.toLowerCase()}
                      {myStore.pitPass && (
                        <img
                          alt="Pitpass icon"
                          className={styles['pitpass-icon']}
                          height="15px"
                          src="https://discounttire.scene7.com/is/content/discounttire/logos/DT_Pit-Pass_bug_red.svg"
                          width="auto"
                        />
                      )}
                    </div>
                    {myStore.pitPass && (
                      <Link className={styles['pitpass-link']} to="/pitpass">
                        Learn more about Pit Pass
                      </Link>
                    )}
                  </>
                )}
              </div>
            )}

            {isArrayWithLength(sortedFilterValues) && (
              <CheckboxGroup groupTitle="Nearby Store Availability">
                <ul className={styles['availability-options']}>
                  {sortedFilterValues.map((filterValue, idx) => (
                    <FilterOption
                      categoryCode={filterValue.categoryCode}
                      categoryName={filterValue.categoryName}
                      isSelected={isSelectedFilter(filterValue)}
                      key={`filteroption-${filterValue.valueCode}-${idx}`}
                      label={getLabel(filterValue)}
                      onClick={onClick}
                      valueCode={filterValue.valueCode}
                      valueName={filterValue.valueName}
                    />
                  ))}
                </ul>
              </CheckboxGroup>
            )}

            {isArrayWithLength(myStore?.nearByStores) &&
              !isChangeStoreDisabled && (
                <div className={styles['nearby-stores']}>
                  <CheckboxGroup
                    groupTitle={otherStoresCopy?.title || 'Selected Stores'}
                  >
                    <ul>
                      {myStore?.nearByStores?.map((store, idx) => (
                        <FilterOption
                          isSelected={nearbyStorePreferences.includes(
                            store?.code
                          )}
                          key={`filteroption-${store?.code}-${idx}`}
                          label={
                            <div className={styles['nearby-store']}>
                              {store?.address?.line1?.toLowerCase()}{' '}
                              <Chip className={styles['store-distance']}>
                                {store?.distance?.toFixed(1)} mi
                              </Chip>
                            </div>
                          }
                          onClick={handleNearbyStoreUpdate}
                          valueCode={store?.code}
                        />
                      ))}
                    </ul>
                  </CheckboxGroup>
                </div>
              )}
          </>
        )}
      </div>
    </CategoryCollapsible>
  );
}

export default GetItFastCategory;
